import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { resolveImage, isColorDark, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';

const MotionDiv = motion.div;

export default function PopupSection({ block = {}, brand, primaryColor, mediaFolders = {}, isPreview }) {
    const { config = {}, content = {} } = block;
    const [visible, setVisible] = useState(!!isPreview);
    const [dismissed, setDismissed] = useState(false);
    const variant = config.variant || 'center';
    const accent = config.accent_color || primaryColor;
    const trigger = config.trigger || 'load';
    const delay = (config.delay || 3) * 1000;
    const imageUrl = resolveImage(content, 'image', mediaFolders) || content.image_url;
    const dialogRef = useRef(null);

    useEffect(() => {
        if (isPreview) return;
        // Only show once per session
        const key = `popup_${block.id || 'default'}_seen`;
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key)) return;

        const show = () => {
            setVisible(true);
            if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, '1');
        };

        if (trigger === 'load') {
            const t = setTimeout(show, 500);
            return () => clearTimeout(t);
        }
        if (trigger === 'delay') {
            const t = setTimeout(show, delay);
            return () => clearTimeout(t);
        }
        if (trigger === 'scroll') {
            const handler = () => {
                const scrollPct = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
                if (scrollPct >= (config.scroll_percent || 30)) {
                    show();
                    window.removeEventListener('scroll', handler);
                }
            };
            window.addEventListener('scroll', handler, { passive: true });
            return () => window.removeEventListener('scroll', handler);
        }
    }, []);

    // Focus dialog when visible & handle Escape key
    useEffect(() => {
        if (!visible || dismissed) return;
        dialogRef.current?.focus();
        const handleKeyDown = (e) => { if (e.key === 'Escape') setDismissed(true); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visible, dismissed]);

    if (!content.title) return null;

    const close = () => setDismissed(true);

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text ? [{ text: content.cta_text, link: content.cta_link || '#', style: 'solid', icon: '', bg_color: '', text_color: '' }] : [];

    const handleBtnClick = (lp) => (e) => { lp.onClick?.(e); close(); };

    // Corner variant
    if (variant === 'corner') {
        return (
            <AnimatePresence>
            {(visible || isPreview) && !dismissed && (
            <MotionDiv
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className={`${isPreview ? 'relative w-full max-w-sm mx-auto' : 'fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9998] max-w-[calc(100vw-2rem)] sm:max-w-sm'}`}>
                <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={content.title} tabIndex={-1}
                    className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                    {imageUrl && (
                        <img src={imageUrl} alt="" className="w-full h-40 object-cover" />
                    )}
                    <div className="p-5">
                        <button onClick={close} aria-label="Cerrar" className="absolute top-3 right-3 p-1 rounded-full bg-black/20 hover:bg-black/40 text-white">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{content.title}</h3>
                        {content.subtitle && <p className="text-sm text-gray-500 mb-4">{content.subtitle}</p>}
                        {sectionButtons.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {sectionButtons.map((btn, i) => {
                                    const btnStyle = btn.style || 'solid';
                                    const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                    const lp = resolveLinkProps(btn.link || '#');
                                    const customBg = btn.bg_color;
                                    const customText = btn.text_color;
                                    let cls = 'px-6 py-2.5 rounded-lg font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                    let style = {};
                                    if (btnStyle === 'solid' || btnStyle === 'pill') {
                                        if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg'; }
                                        else { cls += 'text-white'; style.backgroundColor = accent; }
                                    } else if (btnStyle === 'outline') {
                                        if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                        else { style.color = accent; style.borderColor = accent; cls += 'border-2 hover:opacity-80'; }
                                    } else { style.color = customText || accent; cls += 'hover:opacity-80'; }
                                    if (btnStyle === 'pill') cls = cls.replace('rounded-lg', 'rounded-full');
                                    if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                    return <a key={i} {...lp} onClick={handleBtnClick(lp)} className={cls} style={style}>{icon}{btn.text}</a>;
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </MotionDiv>
            )}
            </AnimatePresence>
        );
    }

    // Slide-in variant
    if (variant === 'slide-in') {
        return (
            <AnimatePresence>
            {(visible || isPreview) && !dismissed && (
            <MotionDiv
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`${isPreview ? 'relative w-full' : 'fixed inset-y-0 right-0 z-[9998]'} flex items-center`}>
                <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={content.title} tabIndex={-1}
                    className="bg-white rounded-l-2xl shadow-2xl max-w-[85vw] sm:max-w-sm w-full overflow-hidden">
                    <button onClick={close} aria-label="Cerrar" className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 z-10">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    {imageUrl && (
                        <img src={imageUrl} alt="" className="w-full h-48 object-cover" />
                    )}
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{content.title}</h3>
                        {content.subtitle && <p className="text-sm text-gray-500 mb-5">{content.subtitle}</p>}
                        {sectionButtons.length > 0 && (
                            <div className="flex flex-col gap-2">
                                {sectionButtons.map((btn, i) => {
                                    const btnStyle = btn.style || 'solid';
                                    const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                    const lp = resolveLinkProps(btn.link || '#');
                                    const customBg = btn.bg_color;
                                    const customText = btn.text_color;
                                    let cls = 'px-6 py-3 rounded-lg font-semibold text-sm transition-all hover:scale-105 inline-flex items-center justify-center gap-2 ';
                                    let style = {};
                                    if (btnStyle === 'solid' || btnStyle === 'pill') {
                                        if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg'; }
                                        else { cls += 'text-white'; style.backgroundColor = accent; }
                                    } else if (btnStyle === 'outline') {
                                        if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                        else { style.color = accent; style.borderColor = accent; cls += 'border-2 hover:opacity-80'; }
                                    } else { style.color = customText || accent; cls += 'hover:opacity-80'; }
                                    if (btnStyle === 'pill') cls = cls.replace('rounded-lg', 'rounded-full');
                                    if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                    return <a key={i} {...lp} onClick={handleBtnClick(lp)} className={cls} style={style}>{icon}{btn.text}</a>;
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </MotionDiv>
            )}
            </AnimatePresence>
        );
    }

    // Center variant (default)
    return (
        <AnimatePresence>
        {(visible || isPreview) && !dismissed && (
        <>
        <MotionDiv initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className={`${isPreview ? '' : 'fixed inset-0 z-[9998] bg-black/50'}`} onClick={isPreview ? undefined : close} />
        <MotionDiv
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className={`${isPreview ? 'relative w-full' : 'fixed inset-0 z-[9998]'} flex items-center justify-center p-4`}
            onClick={isPreview ? undefined : close}>
            <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={content.title} tabIndex={-1}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                <button onClick={close} aria-label="Cerrar" className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 z-10">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                {imageUrl && (
                    <img src={imageUrl} alt="" className="w-full h-52 object-cover" />
                )}
                <div className="p-6 text-center relative">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{content.title}</h3>
                    {content.subtitle && <p className="text-sm text-gray-500 mb-5">{content.subtitle}</p>}
                    {sectionButtons.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-3">
                            {sectionButtons.map((btn, i) => {
                                const btnStyle = btn.style || 'solid';
                                const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                const lp = resolveLinkProps(btn.link || '#');
                                const customBg = btn.bg_color;
                                const customText = btn.text_color;
                                let cls = 'px-8 py-3 rounded-lg font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                let style = {};
                                if (btnStyle === 'solid' || btnStyle === 'pill') {
                                    if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-xl'; }
                                    else { cls += 'text-white shadow-lg hover:shadow-lg'; style.backgroundColor = accent; }
                                } else if (btnStyle === 'outline') {
                                    if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                    else { style.color = accent; style.borderColor = accent; cls += 'border-2 hover:opacity-80'; }
                                } else { style.color = customText || accent; cls += 'hover:opacity-80'; }
                                if (btnStyle === 'pill') cls = cls.replace('rounded-lg', 'rounded-full');
                                if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                return <a key={i} {...lp} onClick={handleBtnClick(lp)} className={cls} style={style}>{icon}{btn.text}</a>;
                            })}
                        </div>
                    )}
                </div>
            </div>
        </MotionDiv>
        </>
        )}
        </AnimatePresence>
    );
}
